// One request every POLL_INTERVAL (0.5s) for all visible widgets: each widget registers its
// data requests (kind + params) while it is on screen, and a single timer posts
// the deduplicated set to POST /ibkr/data. The backend serves them mostly from
// its cache (market data stays subscribed while it's polled).

import { RefObject, useEffect, useState, useSyncExternalStore } from "react";
import { api } from "../utils/api";

const POLL_INTERVAL = 500;
// a widget whose data is older than this shows "stale"
const STALE_AFTER = 4000;
// a poll that hangs (stuck Gateway / backend, stalled network) is given up
// after this, so polling resumes and the widgets turn "stale"
const POLL_TIMEOUT = 4000;

export type DataKind = "quotes" | "positions" | "orders" | "order" | "trades";

interface Result {
  data?: any;
  error?: string;
  at: number;
}

const entries = new Map<string, { kind: DataKind; params: object; count: number }>();
const results = new Map<string, Result>();
let lastPollOk = 0;
let lastPollError: string | null = null;
let version = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;
let rerun = false;
const listeners = new Set<() => void>();

function emit() {
  version++;
  listeners.forEach((l) => l());
}

export function dataKey(kind: DataKind, params: object) {
  return `${kind}:${JSON.stringify(params)}`;
}

async function poll() {
  if (inFlight) {
    rerun = true;
    // re-render anyway: the widgets' age check (STALE_AFTER) runs on render
    emit();
    return;
  }
  if (entries.size === 0) return;
  inFlight = true;
  const requests = Array.from(entries, ([id, e]) => ({ id, kind: e.kind, params: e.params }));
  try {
    const res = await api.post<Record<string, { data?: any; error?: string }>>("/ibkr/data", { requests }, { timeout: POLL_TIMEOUT });
    const now = Date.now();
    for (const [id, r] of Object.entries(res.data)) results.set(id, { ...r, at: now });
    lastPollOk = now;
    lastPollError = null;
  } catch (e: any) {
    lastPollError = e?.response?.data?.detail ?? e?.message ?? "request failed";
  } finally {
    inFlight = false;
    emit();
    if (rerun) {
      rerun = false;
      poll();
    }
  }
}

let kickTimer: ReturnType<typeof setTimeout> | null = null;

// polls right away (coalescing registrations made in the same render)
export function refreshNow() {
  if (kickTimer) return;
  kickTimer = setTimeout(() => {
    kickTimer = null;
    poll();
  }, 30);
}

function register(kind: DataKind, params: object) {
  const key = dataKey(kind, params);
  const entry = entries.get(key);
  if (entry) entry.count++;
  else {
    entries.set(key, { kind, params, count: 1 });
    refreshNow();
  }
  if (!timer) timer = setInterval(poll, POLL_INTERVAL);

  return () => {
    const e = entries.get(key);
    if (e && --e.count === 0) entries.delete(key);
    if (entries.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export type Freshness = "live" | "stale" | "loading";

// data of one request, polled while `enabled` (pass the widget's visibility)
export function useIbkrData<T>(kind: DataKind, params: object, enabled: boolean) {
  const key = dataKey(kind, params);
  useSyncExternalStore(subscribe, () => version);

  useEffect(() => {
    if (!enabled) return;
    return register(kind, JSON.parse(key.slice(kind.length + 1)));
  }, [key, kind, enabled]);

  const result = results.get(key);
  const now = Date.now();
  let freshness: Freshness = "loading";
  if (result) freshness = now - result.at < STALE_AFTER && !lastPollError ? "live" : "stale";
  else if (lastPollError) freshness = "stale";
  return {
    data: result?.data as T | undefined,
    error: result?.error ?? (result ? undefined : lastPollError ?? undefined),
    freshness,
    pollError: lastPollError,
    lastPollOk,
  };
}

// true while the element is (at least partly) inside its scroll container and the viewport
export function useVisible(ref: RefObject<HTMLElement | null>) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}
